import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { CurrentCost, MemberRow, OpKey } from "@/app/groupPerformance";
import { formatRate, type PrimaryBackupRow } from "@/app/health";
import { STRATEGY, type ChannelGroup } from "@/app/master";
import { formatRate as formatShare } from "@/app/traffic";
import { ChannelStatePill, Delta, formatMoney, Pill, Table, TableWrap, Th, type ColWidth } from "@/ui";

interface Props {
  group: ChannelGroup; rows: MemberRow[]; op: OpKey;
  name: (r: MemberRow) => string;
  /** 首选成员的行下标（priority、number_affinity、least_cost），-1 表示没有或没选运营商。 */
  pick: number;
  /** 最低成本与按质量策略的成员成本，按行下标；没选运营商时为空。 */
  costs?: (CurrentCost | null)[];
  /** 加权策略的实际占比，按行下标。 */
  actualShare: (number | null)[];
  /** 所选运营商上的主备状态，没有表示主用。 */
  pbState?: PrimaryBackupRow;
}

/** 策略专属的列：表头、每行的单元格与列宽（缺省数值列 num、其余 104px，#541）。 */
interface Column { head: ReactNode; numeric?: boolean; w?: ColWidth; cell: (r: MemberRow, i: number) => ReactNode }

/** 成员表现表：策略专属的列在前（顺序、权重、成本、时段、主备角色、首发/重试），通用的指标列在后（docs/04 第 8 节 /api/channel-health 的口径）。 */
export function GroupMembersTable(p: Props) {
  const cols = strategyColumns(p);
  return (
    <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th w={240}>成员</Th>{cols.map((c, i) => <Th key={i} numeric={c.numeric} w={c.w ?? (c.numeric ? "num" : 104)}>{c.head}</Th>)}
            <Th w={176}>状态</Th><Th numeric w={88}>实际 TPS</Th><Th numeric w="num">5 分钟送达率</Th><Th numeric w={136}>5 分钟提交错误率</Th><Th numeric w="num">1 小时送达率</Th><Th numeric w="num">1 小时样本</Th><Th numeric>回执延迟</Th>
          </tr>
        </thead>
        <tbody>
          {p.rows.map((r, i) => {
            const s = r.stats;
            return (
              <tr key={i}>
                <td><MemberName r={r} name={p.name} /></td>
                {cols.map((c, k) => <td key={k} className={c.numeric ? "ax-num" : undefined}>{c.cell(r, i)}</td>)}
                <td><ChannelStatePill state={r.state} />{s?.circuit === "limited" && <> <Pill tone="warning" plain>TPS 受限</Pill></>}</td>
                <td className="ax-num">{s ? s.tps.toFixed(1) : "—"}</td>
                <td className="ax-num">{s ? formatRate(s.recent.delivery_rate, s.recent.sample_size) : "—"}</td>
                <td className="ax-num">{s ? formatRate(s.recent.submit_error_rate, s.recent.submit_samples) : "—"}</td>
                <td className="ax-num">{s ? formatRate(s.hour.delivery_rate, s.hour.sample_size) : "—"}</td>
                <td className="ax-num">{s ? s.hour.sample_size : "—"}</td>
                <td className="ax-num">{s?.hour.dlr_latency_ms ? `${(s.hour.dlr_latency_ms / 1000).toFixed(1)} 秒` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableWrap>
  );
}

/** 成员名：通道带编号；子组链接到它自己的详情页，并注明它的策略，指标是它往下所有通道的合计。 */
function MemberName({ r, name }: { r: MemberRow; name: Props["name"] }) {
  if (!r.member.group_id) return <>{name(r)} <span className="ax-mono ax-secondary">#{r.member.channel_id}</span></>;
  return <><Link className="ax-link" to={`/channel-groups/${r.member.group_id}`}>{name(r)}</Link> <span className="ax-secondary">组合 · {STRATEGY[r.group?.strategy ?? ""] ?? r.group?.strategy ?? "已删除"}</span></>;
}

const pickTag = (p: Props, i: number) => (p.pick === i ? <Pill tone={p.rows[i].usable === "ok" ? "brand" : "warning"} plain>{p.rows[i].usable === "ok" ? "当前首选" : "降级首选"}</Pill> : null);

function costText(c: CurrentCost | null | undefined): string {
  return c ? formatMoney(c.price, c.currency) : "未配置";
}

/** 各策略的专属列。 */
function strategyColumns(p: Props): Column[] {
  switch (p.group.strategy) {
    case "priority":
    case "number_affinity":
      return [
        { head: "优先级", numeric: true, cell: (r) => r.member.priority },
        { head: "", cell: (_, i) => pickTag(p, i) },
      ];
    case "least_cost":
      return [
        { head: "优先级", numeric: true, cell: (r) => r.member.priority },
        { head: "每段成本", numeric: true, w: "money", cell: (_, i) => (p.costs ? costText(p.costs[i]) : "—") },
        { head: "", cell: (_, i) => pickTag(p, i) },
      ];
    case "quality_based":
      return [
        { head: "修正送达率", numeric: true, cell: (r) => (r.stats?.adjusted ? formatRate(r.stats.adjusted.delivery_rate, r.stats.adjusted.samples) : "—") },
        { head: "每段成本", numeric: true, w: "money", cell: (_, i) => (p.costs ? costText(p.costs[i]) : "—") },
      ];
    case "weighted": {
      const total = p.rows.reduce((s, r) => s + r.member.weight, 0);
      return [
        { head: "权重", numeric: true, cell: (r) => r.member.weight },
        { head: "配置占比", numeric: true, cell: (r) => (total ? formatShare(r.member.weight / total) : "—") },
        { head: "实际占比", numeric: true, cell: (_, i) => (p.actualShare[i] === null ? "—" : formatShare(p.actualShare[i]!)) },
        // 偏差没有好坏之分，只写带符号的百分点，不用 Delta 的颜色
        { head: "偏差", numeric: true, cell: (r, i) => {
          const a = p.actualShare[i];
          if (a === null || !total) return "—";
          const d = (a - r.member.weight / total) * 100;
          return `${d >= 0 ? "+" : "−"}${Math.abs(d).toFixed(1)}pp`;
        } },
      ];
    }
    case "time":
      return [
        { head: "时段（北京时间）", w: 144, cell: (r) => <span className="ax-mono">{r.member.time_start}–{r.member.time_end}</span> },
        { head: "", cell: (r) => (r.inSlot ? <Pill tone="brand" plain>生效中</Pill> : null) },
      ];
    case "primary_backup":
      return primaryBackupColumns(p);
    case "retry":
      return [{ head: "角色", cell: (_, i) => (i === 0 ? "首发" : "重试") }];
    default:
      return [];
  }
}

/** 主备：成员的配置角色、在所选运营商上的流量角色，以及 5 分钟送达率比主通道高低多少（判定用近 3 分钟，这里只作参考）。 */
function primaryBackupColumns(p: Props): Column[] {
  const primary = p.rows[0]?.stats?.recent;
  const st = p.pbState;
  const active = st?.active_channel_id ?? p.rows[0]?.member.channel_id;
  const flow = (r: MemberRow) => {
    if (p.op === null) return "—";
    if (r.member.channel_id === active) return <Pill tone={st?.state === "switched" ? "warning" : "brand"} plain>当前通道</Pill>;
    if (st?.verify_channel_ids.includes(r.member.channel_id!)) return <Pill tone="brand" plain>验证流量</Pill>;
    return <span className="ax-secondary">小流量</span>;
  };
  return [
    { head: "角色", cell: (_, i) => (i === 0 ? "主通道" : `备用 ${i}`) },
    { head: "流量", cell: flow },
    { head: "较主通道", numeric: true, cell: (r, i) => {
      const s = r.stats?.recent;
      if (i === 0 || !primary?.sample_size || !s?.sample_size) return <Delta direction={0} />;
      const d = (s.delivery_rate - primary.delivery_rate) * 100;
      return <Delta direction={Math.abs(d) < 0.05 ? 0 : d} text={`${Math.abs(d).toFixed(1)}pp`} />; // 上升表示备用比主通道好
    } },
  ];
}
